import { useEffect, useRef, useState } from 'react';

/** Canonical, order-insensitive key for a crop-season selection. */
export const cropSeasonSelectionKey = (
  ids: readonly string[] | undefined
): string => (ids ? [...ids].sort((a, b) => a.localeCompare(b)).join(',') : '');

interface UseSelectionLoadLatchParams {
  /** Key of the current selection; '' when nothing is selected. */
  selectionKey: string;
  /** The summary hook's loading flag. */
  loading: boolean;
  /** Key of the selection the summary data currently held was fetched for. */
  loadedDataKey: string;
}

/**
 * Whether the first summary fetch for the current selection has completed.
 * Two signals, each covering the other's blind spot:
 * - a `loading` true -> false transition seen for this selection: useQuery
 *   reports `loading: false` before its first fetch starts, and a failed
 *   request leaves the previously held data in place;
 * - data tagged with this selection: a cached or synchronously batched
 *   completion never renders `loading: true`.
 * Resets whenever the selection changes, so a switch to another selection
 * reads as not loaded until one of the signals arrives for it.
 */
const useSelectionLoadLatch = ({
  selectionKey,
  loading,
  loadedDataKey
}: UseSelectionLoadLatchParams): boolean => {
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const startedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!selectionKey) {
      startedKey.current = null;
      setSettledKey(null);
      return;
    }
    if (loading) {
      startedKey.current = selectionKey;
    } else if (startedKey.current === selectionKey) {
      setSettledKey(selectionKey);
    }
  }, [loading, selectionKey]);

  return (
    Boolean(selectionKey) &&
    (settledKey === selectionKey || loadedDataKey === selectionKey)
  );
};

export default useSelectionLoadLatch;
